import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Loader2, CheckCircle } from 'lucide-react';
import { useSectionTranslation } from '@/hooks';
import { api } from '@/lib/api';
import { AuthLayout } from '../components/AuthLayout';
import { forgotPasswordTranslations } from './translations';

const schema = z.object({ email: z.string().email().max(254) });
type FormValues = z.infer<typeof schema>;

export function ForgotPassword() {
  const t = useSectionTranslation(forgotPasswordTranslations);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setSending(true);
    await api.post('/auth/forgot-password', values);
    setSending(false);
    setSent(true);
  });

  return (
    <AuthLayout title={t.page}>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-display-md text-brand-white mb-2">{t.heading}</h1>
          <p className="text-body-sm text-brand-muted">{t.subtext}</p>
        </div>

        {sent ? (
          <div className="rounded-xl bg-surface-card border border-surface-border p-6 text-center space-y-4">
            <div className="flex justify-center"><CheckCircle size={40} className="text-brand-success" /></div>
            <h2 className="text-lg font-semibold text-brand-white">{t.successTitle}</h2>
            <p className="text-sm text-brand-muted">{t.successText}</p>
            <Link to="/auth/signin" className="btn-secondary h-10 inline-flex">{t.backToSignIn}</Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate className="space-y-4">
            <div>
              <label className="text-xs font-medium text-brand-muted block mb-1.5">{t.email}</label>
              <div className="flex items-center rounded-lg bg-surface-card border border-surface-border focus-within:border-accent/30 transition-colors">
                <Mail size={14} className="ms-3 text-brand-muted" />
                <input {...register('email')} type="email" placeholder={t.emailPlaceholder} autoComplete="email" className="flex-1 bg-transparent px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none" />
              </div>
              {errors.email && <p className="text-xs text-brand-danger mt-1">{errors.email.message}</p>}
            </div>
            <button type="submit" disabled={sending} className="btn-primary h-11 w-full disabled:opacity-60">
              {sending ? <><Loader2 size={16} className="animate-spin" />{t.submitting}</> : t.submit}
            </button>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}